import { useCallback, useEffect, useRef } from 'react';
import { apiFetch } from '@/lib/api';

// Saving the draft editor's settings as they are changed.
//
// WHY DEBOUNCED, AND WHY `flush` EXISTS. Every keystroke in the header-text box is a state change,
// so saving on each one would be a request per character. Trailing-edge debouncing fixes that but
// introduces a race the launch has to close: a pending save can land AFTER `POST /launch`, which
// would rewrite `launchConfig` to something that was never sent — and the campaign page reads that
// config to render what went out, so the page would then be describing a message nobody received.
//
// Two things prevent it. `flush()` is awaited before launching, and the server refuses a PATCH to a
// launched campaign with a 409. The second is the real guarantee; the first just avoids provoking
// it in the normal case.

const DEBOUNCE_MS = 500;

export interface LaunchConfig {
  channel?: string;
  whatsappTemplateId?: string | null;
  audience?: { mode?: string; contactIds?: string[]; filters?: Record<string, string> };
  message?: string;
  headerMedia?: { assetUid?: string; url?: string; mediaId?: string } | null;
  headerText?: string;
  buttonValues?: Record<string, string>;
}

export function useLaunchConfigAutosave(workspaceId: string, campaignId: string | undefined, enabled: boolean) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<LaunchConfig | null>(null);

  const send = useCallback(async (config: LaunchConfig) => {
    if (!workspaceId || !campaignId) return;
    try {
      await apiFetch(`/workspaces/${workspaceId}/campaigns/${campaignId}`, {
        method: 'PATCH',
        body: JSON.stringify({ launchConfig: config }),
      });
    } catch {
      // Deliberately silent. This is a convenience, not the source of truth for the send: what is
      // launched comes from the request the operator actually makes, so a failed autosave costs
      // them a re-entry after a reload and nothing more. A toast on every transient failure while
      // someone is typing would be worse than the problem.
    }
  }, [workspaceId, campaignId]);

  const save = useCallback((config: LaunchConfig) => {
    if (!enabled) return;
    pending.current = config;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      const next = pending.current;
      pending.current = null;
      if (next) send(next);
    }, DEBOUNCE_MS);
  }, [enabled, send]);

  /** Write any pending change immediately. Await this before launching. */
  const flush = useCallback(async () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const next = pending.current;
    pending.current = null;
    if (next) await send(next);
  }, [send]);

  // A pending save must not fire after the component is gone — by then the campaign may have been
  // launched from somewhere else, and the PATCH would be describing a draft that no longer exists.
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  return { save, flush };
}

export default useLaunchConfigAutosave;
